// =============================================================================
// useClinicPay — hooks for Clinio Pay integration
// =============================================================================
// Linear: CLINIO-200 (PAY-4) + CLINIO-201 (PAY-5)
//
// Wraps the Clinio Pay Edge Functions:
//   - pay-status / pay-onboarding-link / pay-rotate-key  (PAY-4)
//   - pay-create-checkout / pay-refund                   (PAY-5)
//
// Designed to replace (over time) the Stripe-specific hooks in useStripe.ts.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { ApiCallError, type ApiResponse } from '@/lib/api';
import { env } from '@/lib/env';
import { invoiceDetailQueryKey, invoicePaymentsQueryKey } from '@/hooks/useInvoices';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ClinicPayStatus {
  pay_tenant_id: string;
  status: 'pending' | 'onboarding' | 'active' | 'restricted' | 'disabled';
  stripe_account_id: string | null;
  onboarding_completed: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  platform_fee_percent: number;
  test_mode: boolean;
  charge_model: string;
  connect_account_type: string;
  last_synced_at: string | null;
}

/** UI status: Pay tenant "active" ≠ Stripe ready — downgrade until charges work. */
export function deriveClinicPayDisplayStatus(
  payStatus: ClinicPayStatus | null | undefined,
): ClinicPayStatus['status'] | null {
  if (!payStatus) return null;

  const stripeReady =
    payStatus.charges_enabled &&
    payStatus.onboarding_completed &&
    !!payStatus.stripe_account_id;

  if (stripeReady) return 'active';
  if (payStatus.status === 'active' && !stripeReady) return 'onboarding';
  return payStatus.status;
}

export interface ProvisionOutput {
  tenant_id: string;
  status: string;
  already_provisioned?: boolean;
}

export interface OnboardingLinkOutput {
  onboarding_url: string;
}

export interface RotateKeyOutput {
  new_key_valid_until: string;
}

export interface RequestPayLinkOutput {
  checkout_url: string;
  pay_token: string;
  expires_at: string;
  pay_url?: string;
}

export interface RequestPayLinkInput {
  invoice_id: string;
  success_url?: string;
  cancel_url?: string;
}

export interface RequestRefundInput {
  invoice_id: string;
  /** Amount in øre (integer). Must be positive and ≤ invoice total. Cap: 99,999,999. */
  amount: number;
  reason?: string;
}

export interface RequestRefundOutput {
  refund_id: string;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const clinicPayStatusQueryKey = (clinicId: string | null | undefined) =>
  ['clinicPay', 'status', clinicId] as const;

export const clinicPayQueryKey = (invoiceId: string | null | undefined) =>
  ['clinic-pay', 'checkout', invoiceId] as const;

// ── Internal helpers ──────────────────────────────────────────────────────────

/**
 * Invoke `pay-status` (GET) with proper auth headers + query params.
 * `supabase.functions.invoke` uses POST; this helper does a raw fetch for GET.
 */
async function fetchPayStatus(clinicId: string): Promise<ClinicPayStatus | null> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('No active session');

  const url = new URL(
    `${env.VITE_SUPABASE_URL}/functions/v1/pay-status`
  );
  url.searchParams.set('clinic_id', clinicId);

  const resp = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      apikey: env.VITE_SUPABASE_ANON_KEY,
      'x-application-name': 'clinio-web',
    },
  });

  if (!resp.ok) {
    const body = await resp.json().catch(() => ({}));
    throw new Error(
      (body as { error?: { message?: string } }).error?.message ?? `pay-status error ${resp.status}`,
    );
  }

  const body = (await resp.json()) as { ok: boolean; data: ClinicPayStatus | null };
  if (!body.ok) throw new Error('pay-status returned ok=false');
  return body.data;
}

// ── Hooks: PAY-4 (onboarding + status) ────────────────────────────────────────

/**
 * Fetches and syncs the Clinio Pay status for a clinic from the `pay-status`
 * Edge Function (which in turn syncs from Pay).
 *
 * - staleTime 30 s (matches spec).
 * - Polls every 10 s while status is 'onboarding' (waiting for Stripe redirect).
 */
export function useClinicPayStatus(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: clinicPayStatusQueryKey(clinicId),
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    refetchInterval: (query) => {
      return query.state.data?.status === 'onboarding' ? 10_000 : false;
    },
    queryFn: async (): Promise<ClinicPayStatus | null> => {
      if (!clinicId) return null;
      return fetchPayStatus(clinicId);
    },
  });
}

/**
 * Fetches Clinio Pay status from the local DB RPC only (no network call to Pay).
 * Lighter-weight; used when the Edge Function call would be over-budget.
 */
export function useClinicPayStatusLocal(clinicId: string | null | undefined) {
  return useQuery({
    queryKey: [...clinicPayStatusQueryKey(clinicId), 'local'],
    enabled: Boolean(clinicId),
    staleTime: 30_000,
    queryFn: async (): Promise<ClinicPayStatus | null> => {
      if (!clinicId) return null;
      const { data, error } = await supabase.rpc('get_clinic_pay_status', {
        p_clinic: clinicId,
      });
      if (error) throw error;
      if (!data || (Array.isArray(data) && data.length === 0)) return null;
      const row = Array.isArray(data) ? data[0] : data;
      return row as unknown as ClinicPayStatus;
    },
  });
}

/**
 * Mutation: start Clinio Pay onboarding for a clinic.
 * Calls pay-onboarding-link Edge Function, opens the returned URL in a new tab.
 */
export function useStartClinicPayOnboarding(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (opts?: {
      return_url?: string;
      refresh_url?: string;
    }): Promise<OnboardingLinkOutput> => {
      if (!clinicId) throw new Error('clinic_id is required');

      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: OnboardingLinkOutput;
        error?: { code: string; message: string };
      }>('pay-onboarding-link', {
        body: {
          clinic_id: clinicId,
          return_url: opts?.return_url,
          refresh_url: opts?.refresh_url,
        },
      });

      if (error) {
        if (error instanceof FunctionsHttpError) {
          const errBody = (await error.context.json().catch(() => null)) as
            | ApiResponse<OnboardingLinkOutput>
            | null;
          if (errBody?.ok === false) {
            throw new ApiCallError(errBody.error);
          }
        }
        throw error;
      }
      if (!data?.ok || !data.data) throw new Error('pay-onboarding-link returned error');
      return data.data;
    },
    onSuccess: (result) => {
      window.open(result.onboarding_url, '_blank', 'noopener,noreferrer');
      qc.invalidateQueries({ queryKey: clinicPayStatusQueryKey(clinicId) });
    },
  });
}

/**
 * Mutation: rotate the Pay tenant API key.
 * Platform admin only. Calls pay-rotate-key Edge Function (PAY-9 scope;
 * this hook is a placeholder that can be wired once that function ships).
 */
export function useRotateClinicPayKey(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (): Promise<RotateKeyOutput> => {
      if (!clinicId) throw new Error('clinic_id is required');

      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RotateKeyOutput;
      }>('pay-rotate-key', {
        body: { clinic_id: clinicId },
      });

      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('pay-rotate-key returned error');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clinicPayStatusQueryKey(clinicId) });
    },
  });
}

// ── Hooks: PAY-5 (checkout + refund) ──────────────────────────────────────────

/**
 * Request a Clinio Pay checkout link for an invoice.
 *
 * Calls the `pay-create-checkout` Edge Function directly for a reliable
 * synchronous response. Returns `{ checkout_url, pay_token, expires_at }`.
 *
 * Replaces `useRequestStripePayLink` in `useStripe.ts` for clinics using
 * the Clinio Pay integration.
 */
export function useRequestPayLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RequestPayLinkInput): Promise<RequestPayLinkOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RequestPayLinkOutput;
      }>('pay-create-checkout', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) {
        throw new Error('pay-create-checkout returned an unexpected response');
      }
      const result = data.data;
      const payUrl =
        result.pay_url ??
        `${window.location.origin}/pay-invoice/${result.pay_token}`;
      return { ...result, pay_url: payUrl };
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices', vars.invoice_id] });
    },
  });
}

/**
 * Request a Clinio Pay link for an invoice (includes pay_url for the patient page).
 * Calls pay-create-checkout Edge Function directly — avoids pg_net RPC timeout
 * when app.settings.service_role_key is not configured on the database.
 */
export type ClinioPayLinkRequest =
  | string
  | { invoiceId: string; paymentMethod?: 'card' | 'mobilepay' };

function clinioPayLinkInvoiceId(input: ClinioPayLinkRequest): string {
  return typeof input === 'string' ? input : input.invoiceId;
}

export function useRequestClinioPayLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ClinioPayLinkRequest): Promise<RequestPayLinkOutput> => {
      const invoiceId = clinioPayLinkInvoiceId(input);
      const paymentMethod = typeof input === 'string' ? undefined : input.paymentMethod;
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RequestPayLinkOutput;
      }>('pay-create-checkout', {
        body: {
          invoice_id: invoiceId,
          ...(paymentMethod ? { payment_method: paymentMethod } : {}),
        },
      });
      if (error) throw error;
      if (!data?.ok || !data.data) {
        throw new Error('pay-create-checkout returned an unexpected response');
      }
      const result = data.data;
      const payUrl =
        result.pay_url ??
        `${window.location.origin}/pay-invoice/${result.pay_token}`;
      return { ...result, pay_url: payUrl };
    },
    onSuccess: (_result, input) => {
      const invoiceId = clinioPayLinkInvoiceId(input);
      qc.invalidateQueries({ queryKey: ['invoices', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['payment-requests', invoiceId] });
    },
  });
}

export interface CreatePayCheckoutInput {
  invoice_id?: string;
  pay_token?: string;
  success_url?: string;
  cancel_url?: string;
}

export interface CreatePayCheckoutOutput {
  checkout_url: string;
  pay_token?: string;
}

export const CLINIO_PAY_TOKEN_PLACEHOLDER = '__PAY_TOKEN__';

export function payReturnStorageKey(payToken: string) {
  return `clinio:pay-return:${payToken}`;
}

export function buildPayCheckoutReturnUrls(returnPath: string): {
  success_url: string;
  cancel_url: string;
} {
  const origin = window.location.origin;
  const encodedReturn = encodeURIComponent(returnPath);
  return {
    success_url: `${origin}/pay-invoice/${CLINIO_PAY_TOKEN_PLACEHOLDER}?status=succeeded&return_to=${encodedReturn}`,
    cancel_url: `${origin}/pay-invoice/${CLINIO_PAY_TOKEN_PLACEHOLDER}?status=cancelled&return_to=${encodedReturn}`,
  };
}

/**
 * Create or resume a Clinio Pay checkout session.
 * Supports admin (invoice_id + JWT) and public patient (pay_token) flows.
 */
export function useCreatePayCheckout() {
  return useMutation({
    mutationFn: async (input: CreatePayCheckoutInput): Promise<CreatePayCheckoutOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: CreatePayCheckoutOutput;
      }>('pay-create-checkout', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data?.checkout_url) {
        throw new Error('pay-create-checkout returned an unexpected response');
      }
      return data.data;
    },
  });
}

/**
 * Issue a refund for a paid invoice via Clinio Pay.
 *
 * Calls the `pay-refund` Edge Function directly.
 * Requires clinic admin rights.
 *
 * pay-refund writes the invoice ledger before it returns, so the open invoice
 * page refetches status and payment history immediately.
 */
export function useRequestRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RequestRefundInput): Promise<RequestRefundOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok: boolean;
        data: RequestRefundOutput;
      }>('pay-refund', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) {
        throw new Error('pay-refund returned an unexpected response');
      }
      return data.data;
    },
    onSuccess: (_result, vars) => {
      qc.invalidateQueries({ queryKey: invoiceDetailQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: invoicePaymentsQueryKey(vars.invoice_id) });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      qc.invalidateQueries({ queryKey: ['payment-requests', vars.invoice_id] });
    },
  });
}
